import { Row } from '@tanstack/react-table';

import { AlarmSnapshot as AlarmSnapshotSdk } from '@crossnokaye/typescript-sdk/models/components/alarmsnapshot';

import { AlarmActivation, AlarmSnapshot, Severity } from '@repo/sdk/models/components';

import { STATUS_ACTIVE, STATUS_INACTIVE } from './constants';
import {
  AlarmActivationDates,
  AlarmIconConfig,
  AlarmStatus,
  SensorValueData,
  SeverityData,
} from './types';

const priorityOrder = [
  Severity.Critical.toLowerCase(),
  Severity.Warning.toLowerCase(),
  Severity.Caution.toLowerCase(),
];

export const severitySort = <T extends SeverityData>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: string
): number => {
  // a sort function that sorts 'critical' first, followed by 'warning' followed by 'caution'.
  const severityA = (rowA.getValue(columnId) as string).toLowerCase();
  const severityB = (rowB.getValue(columnId) as string).toLowerCase();

  return priorityOrder.indexOf(severityA) - priorityOrder.indexOf(severityB);
};

export const alarmDateSort = <T extends AlarmActivationDates>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: string
): number => {
  // the values of each row are strings in iso date format. Convert them to dates and compare
  const dateA = new Date(rowA.getValue(columnId));
  const dateB = new Date(rowB.getValue(columnId));
  return dateB.getTime() - dateA.getTime();
};

export const numberSort = <T extends SensorValueData>(
  rowA: Row<T>,
  rowB: Row<T>,
  columnId: string
): number => {
  // handle the case where the value is '--', it should put these at the "bottom"
  if (rowA.getValue(columnId) === '--' && rowB.getValue(columnId) === '--') {
    return 0;
  }
  if (rowA.getValue(columnId) === '--') {
    return 1;
  }
  if (rowB.getValue(columnId) === '--') {
    return -1;
  }
  return (rowA.getValue(columnId) as number) - (rowB.getValue(columnId) as number);
};

export const acknowledgedAlarmActivationFilter = (alarmActivation: AlarmActivation): boolean => {
  return (
    !!alarmActivation.acknowledgedAt &&
    ((alarmActivation.severity === Severity.Critical && !alarmActivation.clearedAt) ||
      (([Severity.Caution, Severity.Warning] as Severity[]).includes(alarmActivation.severity) &&
        !alarmActivation.resolvedAt))
  );
};

export const unacknowledgedAlarmActivationFilter = (alarmActivation: AlarmActivation): boolean => {
  return (
    !alarmActivation.acknowledgedAt &&
    ((alarmActivation.severity === Severity.Critical && !alarmActivation.clearedAt) ||
      (([Severity.Caution, Severity.Warning] as Severity[]).includes(alarmActivation.severity) &&
        !alarmActivation.resolvedAt))
  );
};

export const getSnapshotString = (
  snapshot: AlarmSnapshot | AlarmSnapshotSdk | undefined,
  decimalPlaces = 3,
  includeUnit = true
): string => {
  if (!snapshot) {
    return '--';
  }

  // TODO: fix AlarmSnapshotSdk Types to match BFF types
  // Convert the kind value to match the expected format
  let kind = snapshot.kind;
  if (kind === 'bool') {
    kind = 'boolean';
  }

  let value = '--';
  switch (kind) {
    case 'number':
      value = snapshot?.number?.toFixed(decimalPlaces) ?? '--';
      break;
    case 'boolean':
      value = snapshot?.boolean?.toString().toUpperCase() ?? '--';
      break;
    case 'enum':
      value = snapshot?.enum ?? '--';
      break;
    default:
      break;
  }

  if (includeUnit && value !== '--') {
    const unit = snapshot?.unit;
    if (unit) {
      value += ` ${unit}`;
    }
  }

  return value;
};

/**
 * Get the elapsed time between two dates.
 *
 * @param startAt - The start date.
 * @param endAt - The end date. If not provided, the current date is used.
 * @returns The elapsed time in days, hours, and minutes.
 */
export const getElapsedTime = (startAt: Date, endAt?: Date): string => {
  const end: Date = endAt || new Date();

  const diff: number = Math.abs(end.getTime() - startAt.getTime()); // Difference in milliseconds

  const seconds = Math.floor((diff / 1000) % 60); // Remaining seconds
  const minutes = Math.floor((diff / (1000 * 60)) % 60); // Remaining minutes
  const hours = Math.floor((diff / (1000 * 60 * 60)) % 24); // Remaining hours
  const days = Math.floor(diff / (1000 * 60 * 60 * 24)); // Total days

  const parts: string[] = [];

  if (days > 0) {
    parts.push(`${days}d`);
  }
  if (hours > 0) {
    parts.push(`${hours} hrs`);
  }
  if (minutes > 0) {
    parts.push(`${minutes} min`);
  }
  if (seconds > 0) {
    parts.push(`${seconds} sec`);
  }

  // If no parts, return "0 sec" for consistency with existing behavior
  if (parts.length === 0) {
    return '0 sec';
  }

  return parts.join(', ');
};

/**
 * Get the status of an alarm activation.
 *
 * @param alarmActivation - The alarm activation.
 * @returns The status of the alarm activation.
 */
export const getStatus = (alarmActivation: AlarmActivation): AlarmStatus => {
  if (alarmActivation.resolvedAt != undefined) {
    return STATUS_INACTIVE;
  }
  return STATUS_ACTIVE;
};

// TODO: Add these as variants to the Icon component in ux-common?
export const getAlarmIconConfig = (
  severity: Severity,
  isActive: boolean = true
): AlarmIconConfig => {
  const config: AlarmIconConfig = {
    color: 'text-foreground',
    background: 'bg-ckds-base-critical',
    isPulsing: isActive,
  };

  switch (severity) {
    case Severity.Warning:
      config.color = 'text-background';
      config.background = 'bg-ckds-base-warning';
      break;
    case Severity.Caution:
      config.color = 'text-background';
      config.background = 'bg-ckds-base-caution';
      break;
    case Severity.Critical:
      config.color = 'text-foreground';
      config.background = 'bg-ckds-base-critical';
      break;
    default:
      config.color = 'text-foreground';
      config.background = 'bg-input';
      config.isPulsing = false;
  }

  return config;
};
